import type { CommandResult, UiActionCommand, UiActionPlan } from './types'

export type CommandHandlerResult = Omit<CommandResult, 'command_id'>

export type CommandHandlers<TContext> = {
  [TCommand in UiActionCommand as TCommand['type']]: (
    command: TCommand,
    context: TContext
  ) => Promise<CommandHandlerResult> | CommandHandlerResult
}

export async function executeUiActionPlan<TContext>(
  plan: UiActionPlan,
  context: TContext,
  handlers: CommandHandlers<TContext>
): Promise<CommandResult[]> {
  const results: CommandResult[] = []

  for (const [index, command] of plan.commands.entries()) {
    const commandId = command.id ?? `cmd-${index + 1}`

    try {
      const result = await runCommand(command, context, handlers)
      results.push({ command_id: commandId, ...result })
      if (result.status === 'checkpoint') break
    } catch (error) {
      results.push({
        command_id: commandId,
        status: 'error',
        message: error instanceof Error ? error.message : String(error),
      })
      break
    }
  }

  return results
}

function runCommand<TContext>(
  command: UiActionCommand,
  context: TContext,
  handlers: CommandHandlers<TContext>
): Promise<CommandHandlerResult> | CommandHandlerResult {
  const handler = handlers[command.type] as (
    command: UiActionCommand,
    context: TContext
  ) => Promise<CommandHandlerResult> | CommandHandlerResult
  return handler(command, context)
}
